'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './GlobalSideMenu.module.css';
import { Favicon } from '../utils/favicon';
import { useTheme } from '../context/ThemeProvider';

type MenuItem = {
  label: string;
  content: React.ReactNode;
  taskCount?: number;
  showTaskCount?: boolean;
  count?: number; // Count to show in parentheses in label
  icon?: string; // Optional icon path (without .black suffix for dark mode)
  href?: string; // When set, renders as Link instead of tab button
};

type GlobalSideMenuProps = {
  items: MenuItem[];
  initialIndex?: number;
  onChange?: (index: number) => void;
  projectId?: string;
  companyWebsite?: string;
  companyName?: string;
  projectName?: string;
};

export default function GlobalSideMenu({
  items,
  initialIndex = 0,
  onChange,
  projectId,
  companyWebsite,
  companyName,
  projectName
}: GlobalSideMenuProps) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [selected, setSelected] = useState(initialIndex);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const menuItemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Handle initialIndex prop changes
  useEffect(() => {
    setSelected(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const currentMenuItem = menuItemRefs.current[selected];
    const indicator = indicatorRef.current;

    if (currentMenuItem && indicator) {
      const { offsetTop, offsetHeight } = currentMenuItem;
      indicator.style.top = `${offsetTop}px`;
      indicator.style.height = `${offsetHeight}px`;
    }
  }, [selected, items]);

  return (
    <div className={styles.menuList}>

      {/* Header Card */}
      {(projectId || companyWebsite || companyName || projectName) && (
        <div className={styles.headerCard}>

          <div className={styles.headerCardContent}>

            {/* Company icon */}
            {companyWebsite && (
              <div className={styles.companyIcon}>
                <Favicon
                  websiteUrl={companyWebsite}
                  size={16}
                  retinaMultiplier={4}
                />
              </div>
            )}

            {/* Project details */}
            <div className={styles.headerCardText}>

              {/* Company name */}
              {companyName && (
                <div className={styles.companyName}>{companyName}</div>
              )}
              
              {/* Project ID */}
              {projectId && (
                <div className={styles.projectId}>{projectId}</div>
              )}

              {/* Project name */}
              {projectName && (
                <div className={styles.projectName}>{projectName}</div>
              )}

            </div>
          </div>
        </div>
      )}

      {items.map((item, index) => {
        const iconSrc = item.icon
          ? (isDarkMode
              ? item.icon
              : item.icon.includes('.white.')
                ? item.icon.replace('.white.', '.black.')
                : item.icon.endsWith('.svg')
                  ? item.icon.replace('.svg', '.black.svg')
                  : item.icon)
          : null;
        const content = (
          <>
            {item.icon && (
              <img
                src={iconSrc!}
                alt=""
                width={16}
                height={16}
                className={styles.menuIcon}
              />
            )}
            <span className={styles.menuLabel}>
              {item.label}
              {item.count !== undefined && ` (${item.count})`}
            </span>
            {item.taskCount && item.taskCount > 0 && (
              <span className={styles.taskBadge}>
                {item.taskCount > 99 ? '99+' : item.taskCount}
              </span>
            )}
          </>
        );
        if (item.href) {
          return (
            <Link
              key={index}
              to={item.href}
              className={styles.menuItem}
              style={{ textDecoration: 'none' }}
            >
              {content}
            </Link>
          );
        }
        return (
          <button
            key={index}
            ref={el => { menuItemRefs.current[index] = el }}
            className={`${styles.menuItem} ${selected === index ? styles.active : ''}`}
            onClick={() => {
              setSelected(index);
              onChange?.(index);
            }}
          >
            {content}
          </button>
        );
      })}
      <div className={styles.indicator} ref={indicatorRef} />
    </div>
  );
}
